<script setup>
import { LANDING_CONTENT } from '@/services/landing-content';

const steps = LANDING_CONTENT.common.howItWorks;
</script>

<template>
  <section class="how-it-works-section">
    <div class="how-container">
      <h2 class="section-title">How it works</h2>
      <p class="section-subtitle">
        A structured approach to building and maintaining your investment portfolio
      </p>

      <div class="steps-wrapper">
        <div class="steps-connector"></div>
        <div class="steps-container">
          <div
            v-for="step in steps"
            :key="step.step"
            class="step-card"
          >
            <div class="step-number-wrapper">
              <div class="step-number">{{ step.step }}</div>
              <div class="step-glow"></div>
            </div>
            <h3 class="step-title">{{ step.title }}</h3>
            <p class="step-description">{{ step.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.how-it-works-section {
  padding: 80px 24px;
  background: white;
  position: relative;
  overflow: hidden;
}

.how-it-works-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(to right, transparent, #e5e7eb 20%, #e5e7eb 80%, transparent);
}

.how-container {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin: 0 0 16px 0;
  letter-spacing: -0.02em;
}

.section-subtitle {
  font-size: 1.125rem;
  color: #6b7280;
  text-align: center;
  margin: 0 0 64px 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.steps-wrapper {
  position: relative;
}

.steps-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  position: relative;
  z-index: 2;
}

.step-card {
  text-align: center;
  position: relative;
  padding: 24px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.step-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 32px rgba(59, 130, 246, 0.15);
  border-color: #3b82f6;
}

.step-card:hover .step-number {
  transform: scale(1.1);
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
}

.step-card:hover .step-glow {
  opacity: 1;
}

.step-number-wrapper {
  position: relative;
  display: inline-block;
  margin-bottom: 24px;
}

.step-number {
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 2px solid #3b82f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: #3b82f6;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.step-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90px;
  height: 90px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.step-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.step-description {
  font-size: 0.9375rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
}

.steps-connector {
  position: absolute;
  top: 60px;
  left: 12%;
  right: 12%;
  height: 3px;
  background: linear-gradient(to right,
    #3b82f6 0%,
    #10b981 33%,
    #f59e0b 66%,
    #8b5cf6 100%
  );
  z-index: 1;
  opacity: 0.2;
  border-radius: 2px;
}

.steps-connector::before,
.steps-connector::after {
  content: '';
  position: absolute;
  width: 12px;
  height: 12px;
  background: white;
  border: 3px solid #3b82f6;
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%);
}

.steps-connector::before {
  left: -6px;
}

.steps-connector::after {
  right: -6px;
  border-color: #8b5cf6;
}

@media (max-width: 1024px) {
  .steps-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
  }

  .steps-connector {
    display: none;
  }

  .section-title {
    font-size: 2rem;
  }
}

@media (max-width: 768px) {
  .how-it-works-section {
    padding: 48px 16px;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .section-subtitle {
    font-size: 1rem;
    margin-bottom: 40px;
  }

  .steps-container {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .step-number {
    width: 64px;
    height: 64px;
    font-size: 1.375rem;
  }

  .step-glow {
    width: 80px;
    height: 80px;
  }

  .step-card {
    padding: 20px;
  }

  .step-title {
    font-size: 1.0625rem;
  }

  .step-description {
    font-size: 0.9375rem;
  }
}
</style>
